/**
 * @module Model
 */

import { Profile } from '@feature/profiles/shared';
import { CustomFieldValue } from '@app/components/custom-fields/shared/custom-field.model';
import { CompanyType } from '@feature/companies/shared';

/**
 * Class representing a company within the application.
 *
 * This class also provides computed properties to determine
 * if the company is classified as an agent or a customer.
 *
 * @class
 * @model
 */
export class Company {
	/**
	 * The unique identifier for the company.
	 * @type {number}
	 */
	id: number;

	/**
	 * The unique identifier for the owner of the company.
	 * @type {number}
	 */
	ownerId: number;

	/**
	 * The first name of the owner.
	 * @type {string}
	 */
	ownerFirstName: string;

	/**
	 * The middle name of the owner (optional).
	 * @type {string}
	 */
	ownerMiddleName: string;

	/**
	 * The last name of the owner.
	 * @type {string}
	 */
	ownerLastName: string;

	/**
	 * The full name of the owner.
	 * @type {string}
	 */
	ownerFullName: string;

	/**
	 * The email address of the owner.
	 * @type {string}
	 */
	ownerEmail: string;

	/**
	 * The name of the company.
	 * @type {string}
	 */
	name: string;

	/**
	 * A brief description of the company.
	 * @type {string}
	 */
	description: string;

	/**
	 * The time zone in which the company operates.
	 * @type {string}
	 */
	timeZone: string;

	/**
	 * Indicates whether the company is currently active.
	 * @type {boolean}
	 */
	status: boolean;

	/**
	 * Indicates whether the company is classified as an enterprise.
	 * @type {boolean}
	 */
	isEnterprise: boolean;

	/**
	 * The type of the company.
	 * @type {CompanyType}
	 */
	type: CompanyType;

	/**
	 * The profile associated with the company.
	 * @type {Profile}
	 */
	profile: Profile;

	/**
	 * Getter that indicates whether the company is classified as an agent.
	 *
	 * @getter
	 * @returns {boolean} True if the company type is 'agent', otherwise false.
	 */
	get isAgent(): boolean {
		return this.type != null && this.type?.toString() === CompanyType[CompanyType.agent];
	}

	/**
	 * Getter that indicates whether the company is classified as a customer.
	 *
	 * @getter
	 * @returns {boolean} True if the company type is 'customer', otherwise false.
	 */
	get isCustomer(): boolean {
		return this.type != null && this.type?.toString() === CompanyType[CompanyType.customer];
	}
}

/**
 * Class representing an optimized company details.
 *
 * Manage and represent a simplified company data within the application.
 *
 * @class
 * @model
 */
export class CompanyPair {
	/**
	 * The unique identifier for the company.
	 * @type {number}
	 */
	id: number;

	/**
	 * The unique identifier for the owner of the company.
	 * @type {number}
	 */
	ownerId: number;

	/**
	 * The first name of the owner.
	 * @type {string}
	 */
	ownerFirstName: string;

	/**
	 * The middle name of the owner (optional).
	 * @type {string}
	 */
	ownerMiddleName: string;

	/**
	 * The last name of the owner.
	 * @type {string}
	 */
	ownerLastName: string;

	/**
	 * The full name of the owner.
	 * @type {string}
	 */
	ownerFullName: string;

	/**
	 * The email address of the owner.
	 * @type {string}
	 */
	ownerEmail: string;

	/**
	 * The name of the company.
	 * @type {string}
	 */
	name: string;

	/**
	 * A brief description of the company.
	 * @type {string}
	 */
	description: string;

	/**
	 * The time zone in which the company operates.
	 * @type {string}
	 */
	timeZone: string;

	/**
	 * Indicates whether the company is currently active.
	 * @type {boolean}
	 */
	status: boolean;

	/**
	 * Indicates whether the company is classified as an enterprise.
	 * @type {boolean}
	 */
	isEnterprise: boolean;

	/**
	 * Indicates the preference that controls the display options for non-performance payment.
	 * @type {boolean}
	 */
	canBePaidAsVendor?: boolean;

	/**
	 * Indicates the preference that controls the access to non-performance payment.
	 * @type {boolean}
	 */
	canPayOrRequestAsVendor?: boolean;

	/**
	 * Indicates the preference that controls the display options for performance payment request.
	 * @type {boolean}
	 */
	canBeRequestedForPerformance?: boolean;

	/**
	 * The type of the company.
	 * @type {CompanyType}
	 */
	type: CompanyType;

	/**
	 * Optional property representing the company's address.
	 */
	address?: never;

	/**
	 * Optional property representing the company's custom field values.
	 * @type {Array<CustomFieldValue>}
	 */
	customFieldValues?: Array<CustomFieldValue>;
}
